import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as ArrowsUpDownIcon } from '@assets/images/Input/arrows-up-down.svg'
import {
  ActionButtons,
  InputField,
  Modal,
  type InputFieldVariant,
} from '@enamelai/design-system'
import { Icon as DeleteFilledIcon } from '@enamelai/design-system/icons/Delete2'
import { searchProcedure } from '@services/api/procedure.api'
import './AttributeProcedureCodesModal.css'

const PLAIN_TEXT_VARIANT = 'text' as InputFieldVariant

export type AttributedProcedureOption = {
  code: string
  description: string
}

type AttributeProcedureCodesModalProps = {
  open: boolean
  perkLabel: string
  procedures: AttributedProcedureOption[]
  onClose: () => void
  onSave: (procedures: AttributedProcedureOption[]) => void
}

const AttributeProcedureCodesModal = ({
  open,
  perkLabel,
  procedures,
  onClose,
  onSave,
}: AttributeProcedureCodesModalProps): React.ReactElement => {
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState<AttributedProcedureOption[]>(procedures)
  const [results, setResults] = useState<AttributedProcedureOption[]>([])
  const [searching, setSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const searchFieldRef = useRef<HTMLDivElement>(null)
  const [resultsPosition, setResultsPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })

  const title = perkLabel
    ? `Attribute procedure codes - ${perkLabel}`
    : 'Attribute procedure codes'

  const availableResults = useMemo(
    () =>
      results.filter(
        (result) => !draft.some((item) => item.code === result.code),
      ),
    [draft, results],
  )

  useEffect(() => {
    const query = search.trim()
    if (!query) {
      setResults([])
      setSearching(false)
      setHasSearched(false)
      return undefined
    }

    let cancelled = false
    setSearching(true)
    const timer = window.setTimeout(() => {
      void searchProcedure(query, true)
        .then((response) => {
          if (cancelled) return
          const proceduresFound =
            response.statusCode === 200 ? (response.data?.procedures ?? []) : []
          setResults(
            proceduresFound
              .filter((procedure) => procedure.procedure_code)
              .map((procedure) => ({
                code: procedure.procedure_code,
                description: procedure.description ?? '',
              })),
          )
          setSearching(false)
          setHasSearched(true)
        })
        .catch(() => {
          if (cancelled) return
          setResults([])
          setSearching(false)
          setHasSearched(true)
        })
    }, 300)

    const giveUp = window.setTimeout(() => {
      if (cancelled) return
      setSearching(false)
      setHasSearched(true)
    }, 8000)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      window.clearTimeout(giveUp)
    }
  }, [search])

  useEffect(() => {
    if (!search.trim() || !searchFieldRef.current) return undefined

    const placeResults = (): void => {
      const rect = searchFieldRef.current?.getBoundingClientRect()
      if (!rect) return
      setResultsPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      })
    }

    placeResults()
    window.addEventListener('resize', placeResults)
    return () => window.removeEventListener('resize', placeResults)
  }, [search, searching, hasSearched, availableResults.length])

  const addProcedure = (procedure: AttributedProcedureOption): void => {
    setDraft((current) =>
      current.some((item) => item.code === procedure.code)
        ? current
        : [...current, procedure],
    )
    setSearch('')
    setResults([])
    setHasSearched(false)
  }

  return (
    <Modal
      open={open}
      size="md"
      title={title}
      className="attribute-procedures-modal"
      bodyClassName="attribute-procedures-modal__body"
      primaryLabel="Save"
      secondaryLabel="Cancel"
      onClose={onClose}
      onSecondaryClick={onClose}
      onPrimaryClick={() => onSave(draft)}
    >
      <div className="attribute-procedures-modal__search">
        <div
          className="attribute-procedures-modal__search-field"
          ref={searchFieldRef}
        >
          <InputField
            className="attribute-procedures-modal__search-input"
            variant={PLAIN_TEXT_VARIANT}
            fullWidth
            label="Search procedure code to add"
            placeholder="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
          />
          {search.trim() ? (
            <div
              className="attribute-procedures-modal__results"
              role="listbox"
              aria-label="Procedure codes to add"
              style={{
                top: resultsPosition.top,
                left: resultsPosition.left,
                width: resultsPosition.width,
              }}
            >
              {searching ? (
                <p className="attribute-procedures-modal__results-status">
                  Searching...
                </p>
              ) : null}
              {!searching && hasSearched && availableResults.length === 0 ? (
                <p className="attribute-procedures-modal__results-status">
                  No procedure codes to add
                </p>
              ) : null}
              {!searching
                ? availableResults.map((procedure) => (
                    <button
                      key={procedure.code}
                      type="button"
                      role="option"
                      className="attribute-procedures-modal__result"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => addProcedure(procedure)}
                    >
                      {procedure.code}
                      {procedure.description
                        ? ` - ${procedure.description}`
                        : ''}
                    </button>
                  ))
                : null}
            </div>
          ) : null}
        </div>
      </div>
      <div className="attribute-procedures-modal__list">
        <p className="attribute-procedures-modal__list-label">
          List of attributed procedure codes
        </p>
        <div className="attribute-procedures-modal__table" role="table">
          <div
            className="attribute-procedures-modal__row attribute-procedures-modal__row--head"
            role="row"
          >
            <span role="columnheader">
              Procedure codes
              <ArrowsUpDownIcon aria-hidden />
            </span>
            <span role="columnheader">Action</span>
          </div>
          {draft.map((procedure) => (
            <div
              key={procedure.code}
              className="attribute-procedures-modal__row"
              role="row"
            >
              <span>
                {procedure.code}
                {procedure.description ? ` - ${procedure.description}` : ''}
              </span>
              <ActionButtons
                size="sm"
                actions={[
                  {
                    id: 'delete',
                    label: 'Delete',
                    icon: (
                      <DeleteFilledIcon
                        className="ds-action-button__svg"
                        aria-hidden="true"
                      />
                    ),
                    tone: 'danger',
                    variant: 'outline',
                    onClick: () =>
                      setDraft((current) =>
                        current.filter((item) => item.code !== procedure.code),
                      ),
                  },
                ]}
              />
            </div>
          ))}
        </div>
      </div>
    </Modal>
  )
}

export default AttributeProcedureCodesModal
